import React, { useState, useEffect, useCallback } from "react";
import { Box, Typography, Button, IconButton, Paper } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";

const TOOLTIP_WIDTH = 340;
const WELCOME_WIDTH = 420;
const TOOLTIP_GAP = 16;
const SPOT_PADDING = 12;

const STYLES = `
  @keyframes diagCardIn {
    from { opacity: 0; transform: translateY(14px) scale(0.95); }
    to   { opacity: 1; transform: translateY(0) scale(1); }
  }
  @keyframes diagRingPulse {
    0%, 100% { opacity: 1; }
    50%       { opacity: 0.5; }
  }
  @keyframes diagOverlayIn {
    from { opacity: 0; }
    to   { opacity: 1; }
  }
  .diag-card-enter {
    animation: diagCardIn 0.3s cubic-bezier(0.34, 1.4, 0.64, 1) both;
  }
  .diag-overlay-in {
    animation: diagOverlayIn 0.25s ease both;
  }
  .diag-ring {
    animation: diagRingPulse 2.4s ease-in-out infinite;
  }
`;

const clickViewClass = () => {
  const btn = document.querySelector('[data-tour="diag-view-class"] button');
  if (btn) btn.click();
};

const scrollToViewSku = () => {
  const el = document.querySelector('[data-tour="diag-view-sku"]');
  if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
};

const clickViewSku = () => {
  const btn = document.querySelector('[data-tour="diag-view-sku"] button');
  if (btn) btn.click();
};

/** One-time diagnostics tour per logged-in user. */
function getDiagTourStorageKey() {
  if (typeof window === "undefined") return "marketeers_diag_tour_v1__ssr";
  const id = localStorage.getItem("userId");
  return id ? `marketeers_diag_tour_v1__${id}` : "marketeers_diag_tour_v1__anon";
}

const STEPS = [
  {
    icon: "🔍",
    target: null,
    title: "Diagnostics Analysis",
    content:
      "This page helps you identify growth gaps and opportunities across your product portfolio. Drill into brands, SKUs, and stores to uncover where performance can be improved.",
  },
  {
    icon: "🏷️",
    target: '[data-tour="diag-hierarchy"]',
    title: "Hierarchy Filter",
    content:
      "Switch between Brand and Class views to change the level at which products are grouped in the table below. This controls the entire analysis scope.",
  },
  {
    icon: "📋",
    target: '[data-tour="diag-table"]',
    title: "Top Products Table",
    content:
      "Each row shows a product's key performance metrics — Value Share, Volume Share, Growth Index, and more. Color-coded cells make it easy to spot winners and underperformers at a glance.",
  },
  {
    icon: "💡",
    target: '[data-tour="diag-insights"]',
    title: "Market Insights",
    content:
      "AI-generated insights are displayed here — summarizing key findings, underperforming products, and strategic opportunities based on your data.",
  },
  {
    icon: "🔎",
    target: '[data-tour="diag-view-class"]',
    title: "Drill Down by Class",
    content:
      "Click \"View Class\" on any product row to drill down into the sub-categories within that brand. This reveals how individual classes or segments are contributing to overall performance.",
  },
  {
    icon: "📊",
    target: '[data-tour="diag-brand-detail"]',
    title: "Brand Detail View",
    content:
      "After drilling in, you see AI-generated insights for this specific brand alongside Value Sales and Volume Sales donut charts — showing how it compares to the total market.",
    onEnter: clickViewClass,
  },
  {
    icon: "👁️",
    target: '[data-tour="diag-view-sku"]',
    title: "Explore SKUs",
    content:
      "Click \"View\" on any row in the sub-category table to go one level deeper — into individual SKUs and their store-level performance data.",
    onEnter: scrollToViewSku,
  },
  {
    icon: "🏪",
    target: null,
    title: "Store GAPs & Export",
    content:
      "After selecting a SKU, use the store dropdown to compare that SKU's performance against the total market. Export the full store-level dataset as a CSV anytime using the Export button.",
    onEnter: clickViewSku,
  },
];

function computePos(rect, cardWidth) {
  if (!rect) return null;

  const W = window.innerWidth;
  const H = window.innerHeight;
  const { x, y, width, height } = rect;
  const tooltipH = 280;

  if (W - (x + width + SPOT_PADDING) >= cardWidth + TOOLTIP_GAP) {
    return {
      top: Math.max(8, Math.min(y - SPOT_PADDING, H - tooltipH - 8)),
      left: x + width + SPOT_PADDING + TOOLTIP_GAP,
    };
  }
  if (x - SPOT_PADDING >= cardWidth + TOOLTIP_GAP) {
    return {
      top: Math.max(8, Math.min(y - SPOT_PADDING, H - tooltipH - 8)),
      left: x - SPOT_PADDING - TOOLTIP_GAP - cardWidth,
    };
  }
  if (H - (y + height + SPOT_PADDING) >= tooltipH + TOOLTIP_GAP) {
    return {
      top: y + height + SPOT_PADDING + TOOLTIP_GAP,
      left: Math.max(8, Math.min(x, W - cardWidth - 8)),
    };
  }
  return {
    top: Math.max(8, y - SPOT_PADDING - tooltipH - TOOLTIP_GAP),
    left: Math.max(8, Math.min(x, W - cardWidth - 8)),
  };
}

export default function DiagnosticsTour() {
  const isFreeTrial = localStorage.getItem("role") === "free_trial";
  const [active, setActive] = useState(false);
  const [step, setStep] = useState(0);
  const [rect, setRect] = useState(null);
  const [cardKey, setCardKey] = useState(0);

  useEffect(() => {
    if (isFreeTrial) return undefined;
    if (localStorage.getItem(getDiagTourStorageKey()) === "1") {
      return undefined;
    }

    const tryStart = () => {
      if (localStorage.getItem(getDiagTourStorageKey()) === "1") return;
      if (window.__mainTourActive) return;
      setActive(true);
    };

    const t = setTimeout(tryStart, 1600);
    let dismissTimer;
    const onMainDismissed = () => {
      if (dismissTimer) clearTimeout(dismissTimer);
      dismissTimer = setTimeout(tryStart, 400);
    };
    window.addEventListener("tour:mainDismissed", onMainDismissed);

    return () => {
      clearTimeout(t);
      if (dismissTimer) clearTimeout(dismissTimer);
      window.removeEventListener("tour:mainDismissed", onMainDismissed);
    };
  }, []);

  const currentStep = STEPS[step];

  const refreshRect = useCallback(() => {
    if (!currentStep?.target) {
      setRect(null);
      return;
    }
    const el = document.querySelector(currentStep.target);
    setRect(el ? el.getBoundingClientRect() : null);
  }, [currentStep]);

  useEffect(() => {
    if (!active) return;
    setCardKey((k) => k + 1);

    if (currentStep.onEnter) {
      currentStep.onEnter();
      const t = setTimeout(refreshRect, 600);
      window.addEventListener("resize", refreshRect);
      window.addEventListener("scroll", refreshRect, true);
      return () => {
        clearTimeout(t);
        window.removeEventListener("resize", refreshRect);
        window.removeEventListener("scroll", refreshRect, true);
      };
    }

    refreshRect();
    window.addEventListener("resize", refreshRect);
    window.addEventListener("scroll", refreshRect, true);
    return () => {
      window.removeEventListener("resize", refreshRect);
      window.removeEventListener("scroll", refreshRect, true);
    };
  }, [active, refreshRect, step]);

  const complete = useCallback(() => {
    localStorage.setItem(getDiagTourStorageKey(), "1");
    setActive(false);
    window.dispatchEvent(new CustomEvent("tour:resetDiagnostics"));
  }, []);

  const next = () =>
    step < STEPS.length - 1 ? setStep((s) => s + 1) : complete();
  const back = () => step > 0 && setStep((s) => s - 1);

  if (isFreeTrial || !active) return null;

  const isCenter = !rect;
  const isFirst = step === 0;
  const isLast = step === STEPS.length - 1;
  const cardWidth = isFirst ? WELCOME_WIDTH : TOOLTIP_WIDTH;
  const tooltipPos = computePos(rect, cardWidth);
  const W = window.innerWidth;
  const H = window.innerHeight;
  const progress = ((step + 1) / STEPS.length) * 100;

  return (
    <Box sx={{ position: "fixed", inset: 0, zIndex: 10000, pointerEvents: "none" }}>
      <style>{STYLES}</style>

      {/* Overlay */}
      {isCenter ? (
        <Box
          className="diag-overlay-in"
          sx={{
            position: "absolute",
            inset: 0,
            bgcolor: "rgba(3,10,30,0.72)",
            backdropFilter: "blur(2px)",
            pointerEvents: "all",
          }}
          onClick={complete}
        />
      ) : (
        <svg
          className="diag-overlay-in"
          style={{ position: "absolute", top: 0, left: 0, width: "100%", height: "100%" }}
          xmlns="http://www.w3.org/2000/svg"
        >
          <defs>
            <mask id="diag-mask">
              <rect width={W} height={H} fill="white" />
              <rect
                x={rect.x - SPOT_PADDING}
                y={rect.y - SPOT_PADDING}
                width={rect.width + SPOT_PADDING * 2}
                height={rect.height + SPOT_PADDING * 2}
                rx="10"
                fill="black"
              />
            </mask>
            <filter id="diag-glow" x="-20%" y="-20%" width="140%" height="140%">
              <feGaussianBlur stdDeviation="4" result="blur" />
              <feMerge>
                <feMergeNode in="blur" />
                <feMergeNode in="SourceGraphic" />
              </feMerge>
            </filter>
          </defs>

          <rect
            width={W}
            height={H}
            fill="rgba(3,10,30,0.72)"
            mask="url(#diag-mask)"
          />
          <rect
            x={rect.x - SPOT_PADDING - 4}
            y={rect.y - SPOT_PADDING - 4}
            width={rect.width + (SPOT_PADDING + 4) * 2}
            height={rect.height + (SPOT_PADDING + 4) * 2}
            rx="13"
            fill="none"
            stroke="rgba(87,80,255,0.3)"
            strokeWidth="8"
            filter="url(#diag-glow)"
          />
          <rect
            className="diag-ring"
            x={rect.x - SPOT_PADDING}
            y={rect.y - SPOT_PADDING}
            width={rect.width + SPOT_PADDING * 2}
            height={rect.height + SPOT_PADDING * 2}
            rx="10"
            fill="none"
            stroke="#5750FF"
            strokeWidth="2.5"
          />
        </svg>
      )}

      {/* Tooltip card */}
      <Box
        sx={
          isCenter
            ? {
                position: "fixed",
                inset: 0,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                pointerEvents: "none",
              }
            : {
                position: "fixed",
                top: tooltipPos?.top,
                left: tooltipPos?.left,
                pointerEvents: "none",
              }
        }
      >
        <Paper
          key={cardKey}
          className="diag-card-enter"
          elevation={0}
          sx={{
            width: cardWidth,
            pointerEvents: "all",
            borderRadius: "16px",
            overflow: "hidden",
            boxShadow:
              "0 32px 64px rgba(0,0,0,0.28), 0 8px 24px rgba(0,0,0,0.16)",
            border: "1px solid rgba(255,255,255,0.9)",
            bgcolor: "#fff",
          }}
        >
          {/* Gradient accent bar */}
          <Box
            sx={{
              height: 4,
              background: "linear-gradient(90deg, #00265E 0%, #5750FF 100%)",
            }}
          />

          {/* Welcome hero (first step only) */}
          {isFirst && (
            <Box
              sx={{
                height: 110,
                background:
                  "linear-gradient(135deg, #00265E 0%, #1a3f7a 50%, #5750FF 100%)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                position: "relative",
                overflow: "hidden",
              }}
            >
              {[
                { top: -30, right: -20, size: 110 },
                { bottom: -25, left: -15, size: 90 },
                { top: 20, left: "30%", size: 60 },
              ].map((b, i) => (
                <Box
                  key={i}
                  sx={{
                    position: "absolute",
                    top: b.top,
                    bottom: b.bottom,
                    left: b.left,
                    right: b.right,
                    width: b.size,
                    height: b.size,
                    borderRadius: "50%",
                    bgcolor: "rgba(255,255,255,0.07)",
                  }}
                />
              ))}
              <Typography
                sx={{
                  fontSize: 44,
                  lineHeight: 1,
                  filter: "drop-shadow(0 4px 10px rgba(0,0,0,0.3))",
                }}
              >
                🔍
              </Typography>
            </Box>
          )}

          {/* Card body */}
          <Box sx={{ px: 3, pt: isFirst ? 2.5 : 2, pb: 0 }}>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                mb: 1.25,
              }}
            >
              {!isFirst && (
                <Box sx={{ fontSize: 22, lineHeight: 1 }}>
                  {currentStep.icon}
                </Box>
              )}
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, ml: "auto" }}>
                <Typography
                  sx={{
                    fontSize: 11,
                    color: "#A0ADB8",
                    fontFamily: "Poppins",
                    fontWeight: 600,
                    letterSpacing: "0.6px",
                    textTransform: "uppercase",
                  }}
                >
                  {step + 1} / {STEPS.length}
                </Typography>
                <IconButton
                  size="small"
                  onClick={complete}
                  sx={{
                    p: 0.4,
                    ml: 0.5,
                    color: "#C5CDD6",
                    "&:hover": { color: "#444", bgcolor: "#F5F6F8" },
                  }}
                >
                  <CloseIcon sx={{ fontSize: 15 }} />
                </IconButton>
              </Box>
            </Box>

            <Typography
              sx={{
                fontWeight: 700,
                fontSize: isFirst ? 20 : 16.5,
                color: "#0A1929",
                fontFamily: "Poppins",
                lineHeight: 1.3,
                mb: 1,
              }}
            >
              {currentStep.title}
            </Typography>

            <Typography
              sx={{
                fontSize: 13.5,
                color: "#637381",
                fontFamily: "Poppins",
                lineHeight: 1.75,
                mb: 2,
              }}
            >
              {currentStep.content}
            </Typography>
          </Box>

          {/* Progress bar */}
          <Box sx={{ px: 3, pb: 2 }}>
            <Box
              sx={{
                height: 3,
                bgcolor: "#EEF0F5",
                borderRadius: 2,
                overflow: "hidden",
              }}
            >
              <Box
                sx={{
                  height: "100%",
                  width: `${progress}%`,
                  background: "linear-gradient(90deg, #00265E 0%, #5750FF 100%)",
                  borderRadius: 2,
                  transition: "width 0.45s cubic-bezier(0.4, 0, 0.2, 1)",
                }}
              />
            </Box>
          </Box>

          {/* Footer */}
          <Box
            sx={{
              px: 3,
              pb: 2.5,
              pt: 0.5,
              display: "flex",
              alignItems: "center",
              justifyContent: step > 0 ? "space-between" : "flex-end",
            }}
          >
            {step > 0 && (
              <Button
                startIcon={<ArrowBackIcon sx={{ fontSize: 14 }} />}
                onClick={back}
                sx={{
                  textTransform: "none",
                  color: "#8796A5",
                  fontFamily: "Poppins",
                  fontSize: 13.5,
                  fontWeight: 500,
                  px: 1.5,
                  borderRadius: "8px",
                  "&:hover": { bgcolor: "#F5F6F8", color: "#00265E" },
                }}
              >
                Back
              </Button>
            )}

            <Button
              variant="contained"
              endIcon={!isLast ? <ArrowForwardIcon sx={{ fontSize: 14 }} /> : undefined}
              onClick={next}
              sx={{
                textTransform: "none",
                background: isLast
                  ? "linear-gradient(135deg, #5750FF 0%, #7B75FF 100%)"
                  : "linear-gradient(135deg, #00265E 0%, #1a4080 100%)",
                fontFamily: "Poppins",
                fontSize: 13.5,
                fontWeight: 600,
                px: 2.5,
                py: 0.9,
                borderRadius: "8px",
                boxShadow: isLast
                  ? "0 4px 14px rgba(87,80,255,0.4)"
                  : "0 4px 14px rgba(0,38,94,0.3)",
                transition: "all 0.2s ease",
                "&:hover": {
                  transform: "translateY(-1px)",
                  boxShadow: isLast
                    ? "0 6px 18px rgba(87,80,255,0.5)"
                    : "0 6px 18px rgba(0,38,94,0.4)",
                },
              }}
            >
              {isLast ? "Start Exploring" : "Next"}
            </Button>
          </Box>
        </Paper>
      </Box>

      {/* Skip pill */}
      {!isLast && (
        <Box
          sx={{
            position: "fixed",
            bottom: 28,
            left: "50%",
            transform: "translateX(-50%)",
            pointerEvents: "all",
          }}
        >
          <Button
            onClick={complete}
            sx={{
              color: "rgba(255,255,255,0.75)",
              textTransform: "none",
              fontFamily: "Poppins",
              fontSize: 13,
              fontWeight: 500,
              px: 3,
              py: 0.75,
              borderRadius: "999px",
              border: "1.5px solid rgba(255,255,255,0.3)",
              bgcolor: "rgba(0,0,0,0.25)",
              backdropFilter: "blur(6px)",
              letterSpacing: "0.2px",
              transition: "all 0.2s ease",
              "&:hover": {
                color: "#fff",
                bgcolor: "rgba(255,255,255,0.12)",
                border: "1.5px solid rgba(255,255,255,0.7)",
              },
            }}
          >
            Skip tour
          </Button>
        </Box>
      )}
    </Box>
  );
}
